import { ConfluenceClient } from '@canvas/atlassian-client/confluence';

import {
  type AddCommentOpts,
  type UpdateCommentOpts,
  type ListCommentOpts,
  type DeleteCommentOpts,
  type ResolveCommentOpts,
  type CommentReactionOpts,
  type ListCommentReactionOpts,
  type BoardTitleOpts,
  type CommentCountOpts,
  type MentionNotificationOpts,
  type ContentPermissionsOpts,
} from './confluence-service-types';

export class ConfluenceService {
  public constructor(
    private client = new ConfluenceClient(),
    private readonly origin = new URL(globalThis.location.origin)
  ) {}

  private getBaseUrl() {
    const base = this.origin.toString();
    if (base.endsWith('/')) {
      return base.slice(0, -1);
    }

    return base;
  }

  public addComment({
    boardId,
    content,
    parentCommentId,
    createdFrom,
    originalSelection = '',
  }: AddCommentOpts) {
    return this.client.addComment({
      baseUrl: this.getBaseUrl(),
      containerId: boardId,
      content,
      parentCommentId,
      createdFrom,
      originalSelection,
    });
  }

  public updateComment({ commentId, content }: UpdateCommentOpts) {
    return this.client.updateComment({
      baseUrl: this.getBaseUrl(),
      content,
      commentId,
    });
  }

  public deleteComment({ commentId }: DeleteCommentOpts) {
    return this.client.deleteComment({
      baseUrl: this.getBaseUrl(),
      commentId,
    });
  }

  public resolveComment({ commentId, resolved }: ResolveCommentOpts) {
    return this.client.resolveComment({
      baseUrl: this.getBaseUrl(),
      commentId,
      resolved,
    });
  }

  public listComments({
    boardId,
    parentCommentId: commentId,
    noCache = false,
    before,
  }: ListCommentOpts) {
    return this.client.listComments({
      baseUrl: this.getBaseUrl(),
      containerId: boardId,
      parentCommentId: commentId,
      noCache: noCache,
      before,
    });
  }

  public getCommentCount({ boardId, noCache = false }: CommentCountOpts) {
    return this.client.getCommentCount({
      baseUrl: this.getBaseUrl(),
      containerId: boardId,
      noCache: noCache,
    });
  }

  public addCommentReaction({ containerId, commentId, emojiId }: CommentReactionOpts) {
    return this.client.addCommentReaction({
      baseUrl: this.getBaseUrl(),
      containerId,
      commentId,
      emojiId,
    });
  }

  public deleteCommentReaction({ containerId, commentId, emojiId }: CommentReactionOpts) {
    return this.client.deleteCommentReaction({
      baseUrl: this.getBaseUrl(),
      containerId,
      commentId,
      emojiId,
    });
  }

  public listCommentReactions(comments: ListCommentReactionOpts[]) {
    return this.client.listCommentReactions({
      baseUrl: this.getBaseUrl(),
      comments,
    });
  }

  public getCommentReactionDetails({
    containerId,
    commentId,
    emojiId,
  }: CommentReactionOpts) {
    return this.client.getCommentReactionDetails({
      baseUrl: this.getBaseUrl(),
      containerId,
      commentId,
      emojiId,
    });
  }

  public getBoardTitle({ contentId }: BoardTitleOpts) {
    return this.client.getWhiteboardBoardTitle({
      baseUrl: this.getBaseUrl(),
      contentId,
    });
  }

  public toReferenceId(contentId: string) {
    return this.client.toReferenceId(contentId);
  }

  public createMentionNotification({ contentId, mentions }: MentionNotificationOpts) {
    return this.client.createMentionNotification({
      baseUrl: this.getBaseUrl(),
      contentId,
      mentions,
    });
  }

  public getContentPermissions({ accountIds, contentId }: ContentPermissionsOpts) {
    return this.client.getContentPermissions({
      baseUrl: this.getBaseUrl(),
      accountIds,
      contentId,
    });
  }
}

let confluenceService: ConfluenceService;

export const getConfluenceService = () => {
  if (!confluenceService) {
    confluenceService = new ConfluenceService();
  }

  return confluenceService;
};
